import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Loading from "common/components/Elements/Loading/Loading";
import { consoleInfo } from "common/utilities/browser/consoleLog/consoleLog";
import { arrayInsert, arrayLast, arraySymmetricDiff } from "common/utilities/core/array";
import { deepClone } from "common/utilities/core/object";
import { DEBOUNCE_TIME } from "common/utilities/timeout/timeout";
import type { RouterProps } from "common/utilities/withRouter/withRouter";
import { withRouter } from "common/utilities/withRouter/withRouter";
import type * as I from "core/api/coreApiClient";
import HeaderMenu from "core/components/App/SimpleHeader/SimpleHeader";
import ErrorPage from "core/components/Screens/ErrorPage/ErrorPage";
import { enqueueWarningNotification } from "core/redux/actions/notification/notificationActions";
import type { EnqueueNotification } from "core/redux/actions/notification/notificationActions.types";
import { checkSessionAlive, userSessionExpired } from "core/redux/actions/user/userActions";
import type { ApplicationState } from "core/redux/reducers/rootReducer";
import type { AppDispatch, AppThunkDispatch } from "core/redux/store/configureStore.types";
import { formatDatetime } from "core/utilities/dateFormat/dateFormat";
import debounce from "debounce";
import I18n from "i18n/en-US";
import queryString from "query-string";
import React from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { connect } from "react-redux";
import { push } from "redux-first-history";
import { alignSequences, getSession, sequenceFeatureColors } from "sequenceAnalyser/api/api";
import type {
  AlignmentOptions,
  EntitySequence as Sequence,
  SequenceAlign,
  SequenceFeatureColor,
  SequenceType,
  Session as SequenceAnalyserSession,
} from "sequenceAnalyser/api/sequenceAnalyserApiClient";
import BrowserZoomLevelWarning from "sequenceAnalyser/components/BrowserZoomLevelWarning/BrowserZoomLevelWarning";
import PrintView from "sequenceAnalyser/components/Sequences/PrintView/PrintView";
import SequenceTable from "sequenceAnalyser/components/Sequences/SequenceTable/SequenceTable";
import SequenceTableVisualisations from "sequenceAnalyser/components/Sequences/SequenceTableVisualisations/SequenceTableVisualisations";
import SequencesNavigatorPosition from "sequenceAnalyser/components/SequencesNavigatorPosition/SequencesNavigatorPosition";
import TableActions from "sequenceAnalyser/components/TableActions/TableActions";
import TableActionsHeader from "sequenceAnalyser/components/TableActions/TableActionsHeader/TableActionsHeader";
import {
  clearSelection,
  createSelection,
  updateHasColorsAndFeaturesVisible,
} from "sequenceAnalyser/redux/actions/sequenceAnalyserActions";
import type { ColumnsLetterOccurrences } from "sequenceAnalyser/utilities/colorCalculations/colorCalculations";
import { calculateLetterOccurrences } from "sequenceAnalyser/utilities/colorCalculations/colorCalculations";
import type { SelectedColorScheme } from "sequenceAnalyser/utilities/colors";
import {
  createFeatureDefinitions,
  formatSequencesFeaturesPositionsToAlignedPositions,
  sortSequencesFeaturesByLength,
} from "sequenceAnalyser/utilities/features/features";
import { headerPixelHeight } from "sequenceAnalyser/utilities/screen/screen";
import type { SequencesSearch, SequencesSearchScope } from "sequenceAnalyser/utilities/search/search";
import { searchSequences } from "sequenceAnalyser/utilities/search/search";
import type { SequenceSelection } from "sequenceAnalyser/utilities/selection/selection";
import type {
  FeatureDefinitions,
  FeatureTypesColor,
  SequenceAnalyserTableView,
} from "sequenceAnalyser/utilities/sequenceAnalyser.types";
import { SequenceAnalyserTableViews } from "sequenceAnalyser/utilities/sequenceAnalyser.types";
import {
  ensureAlignedSequencesSimilarLength,
  hasAnyFeatureDefinitionVisible,
} from "sequenceAnalyser/utilities/sequenceAnalyser/sequenceAnalyser";

import classes from "./SequenceAnalyser.styles";

/** Set to true when working on the printing view and need instant feedback on the view */
export const SHOW_PRINT_VIEW = false;

export type HandleAlignedSequencesChange = (
  alignedSequences: SequenceAlign[],
  sequences?: Sequence[],
  newSelection?: SequenceSelection,
) => void;

export type HandleInsertSequences = (
  newRawSequences: Sequence[],
  newSequences: Sequence[],
  newAlignedSequences: SequenceAlign[],
  insertPosition: number,
  sequenceType?: SequenceType,
) => void;

export type HandleReplaceSequences = (
  newRawSequences: Sequence[],
  newSequences: Sequence[],
  newAlignedSequences: SequenceAlign[],
  toggledOppositeStrands: number[],
) => void;

export type HandleReplaceAlignedSequences = (alignedSequencesToReplace: SequenceAlign[]) => void;

export type HandleFeatureDefinitionsChange = (
  featureDefinitions: FeatureDefinitions,
  featureTypesColor: FeatureTypesColor,
  userColors: SequenceFeatureColor[],
) => void;

export type HandleRealignment = (options: AlignmentOptions) => Promise<void>;

interface PropsFromState {
  configuration: I.AppConfiguration;
  sessionExpired?: boolean;
  ranAlignments: AlignmentOptions[];
}

interface ComponentProps extends RouterProps, PropsFromState {
  dispatchUpdateHasColorsAndFeaturesVisible: (hasColorsAndFeaturesVisible: boolean) => void;
  dispatchCreateSelection: (selection: SequenceSelection) => void;
  dispatchClearSelection: () => void;
  dispatchCheckSessionAlive: (sessionId: string) => void;
  dispatchEnqueueWarningNotification: EnqueueNotification;
  dispatchSessionExpired: () => void;
}

interface ComponentState {
  loadingSession: boolean;
  runningAlignment: boolean;
  /** Raw sequences data from the API (or manually added), existing data never changes, only new sequences can be added  */
  rawSequences: Sequence[];
  /** Raw sequences that got their features position adjusted following manipulation (alignment, adjusted gaps) */
  sequences: Sequence[];
  /**
   * Aligned sequences: just id and residues, rest of the data stays in 'sequences' variable to avoid duplicates.
   * Note that SequenceAlign id equal the Sequence id.
   */
  alignedSequences: SequenceAlign[];
  featureDefinitions: FeatureDefinitions;
  /** Cache of feature types colors to be re-used when re-rendering so we don't randomly assign different colors */
  featureTypesColor: FeatureTypesColor;
  /** Residues colors */
  colors?: SelectedColorScheme;
  columnsLetterOccurrences: ColumnsLetterOccurrences;
  view: SequenceAnalyserTableView;
  search: SequencesSearch;
  alignmentVersion: number;
  sessionId?: string;
  sessionError: boolean;
  printing: boolean;
  /** List of sequences ids expanded  */
  expandedFeaturesForSequences: number[];
  session?: SequenceAnalyserSession;
  oppositeStrands: number[];
  /** Have the shown sequences been aligned */
  activeAlignment: boolean;
  userColors: SequenceFeatureColor[];
}

export class SequenceAnalyser extends React.Component<ComponentProps, ComponentState> {
  static formatSequencesFeatures = (sequences: Sequence[], alignedSequences: SequenceAlign[]) =>
    sortSequencesFeaturesByLength(formatSequencesFeaturesPositionsToAlignedPositions(sequences, alignedSequences));

  /** Ensure the user session is still valid after the mouse is idle for 5min  */
  private debouncedEnsureSessionIsValid: () => void;

  /** Show running alignment loading screen if it takes too long */
  private runningAlignmentTimer: ReturnType<typeof setTimeout> | undefined;

  constructor(props: ComponentProps) {
    super(props);

    this.state = {
      loadingSession: true,
      runningAlignment: false,
      rawSequences: [],
      sequences: [],
      alignedSequences: [],
      featureDefinitions: {},
      featureTypesColor: {},
      columnsLetterOccurrences: {},
      view: SequenceAnalyserTableViews.default,
      search: { query: "", scope: "all", matches: {} },
      alignmentVersion: 0,
      sessionError: false,
      printing: false,
      expandedFeaturesForSequences: [],
      oppositeStrands: [],
      activeAlignment: false,
      userColors: [],
    };

    document.title = this.buildDocumentTitle();

    this.debouncedEnsureSessionIsValid = debounce(
      () => this.ensureSessionIsValid(),
      DEBOUNCE_TIME.ensureSessionIsValid,
    );
  }

  async componentDidMount() {
    consoleInfo(
      "Warning Note: having the developer tools opened creates a slower render time for the Sequence Viewer.",
    );

    const { sessionId, sessionError } = this.state;

    if (!sessionError) {
      if (sessionId && sessionId.length > 0) {
        try {
          const sessionResponse = await getSession(sessionId);

          if (!sessionResponse.payload) return;

          const userColors = await sequenceFeatureColors(sessionResponse.payload.sequenceType);

          const rawSequences = sessionResponse.payload.entitySequences;

          // Either show:
          // - already aligned sequences
          // or
          // - entity sequences as aligned sequences to render first the view (subsequent alignment is run by componentDidUpdate)
          const alignedSequences =
            sessionResponse.payload.alignedSequences ||
            sessionResponse.payload.entitySequences.map(({ id, residues }) => ({ id, residues }));

          const formattedSequences = SequenceAnalyser.formatSequencesFeatures(rawSequences, alignedSequences);

          this.setSequences(rawSequences, formattedSequences, alignedSequences, {
            session: sessionResponse.payload,
            activeAlignment: sessionResponse.payload.alignedSequences !== undefined,
            userColors,
          });

          window.addEventListener("afterprint", this.handleAfterPrint);
          document.addEventListener("mousemove", this.debouncedEnsureSessionIsValid);
        } catch {
          this.setState({ sessionError: true });
        }
      } else {
        this.setState({ sessionError: true });
      }
    }
  }

  componentDidUpdate(prevProps: ComponentProps, prevState: ComponentState) {
    const { printing: prevPrinting } = prevState;
    const { printing, session } = this.state;
    const { navigate, location, sessionExpired, dispatchEnqueueWarningNotification, ranAlignments } = this.props;

    // Run alignment if the session didn't return aligned sequences already
    if (session && !prevState.session && !session.alignedSequences) {
      // this.alignRawSequences();
    }

    if (printing && !prevPrinting) {
      document.title = this.buildDocumentTitle(true);
      // Remove parameters from the URL as the URL is shown within the print footer
      navigate(location.pathname.split("?")[0], { replace: true });
      window.print();
    } else {
      document.title = this.buildDocumentTitle();
    }

    if (sessionExpired && !prevProps.sessionExpired) {
      dispatchEnqueueWarningNotification(I18n.SequenceAnalyser.SequenceAnalyser.sessionExpired);
    }

    if (ranAlignments.length !== prevProps.ranAlignments.length) {
      this.handleRealignment(arrayLast(ranAlignments));
    }
  }

  componentWillUnmount() {
    window.removeEventListener("afterprint", this.handleAfterPrint);
    document.removeEventListener("mousemove", this.debouncedEnsureSessionIsValid);
  }

  static getDerivedStateFromProps(props: ComponentProps, state: ComponentState): ComponentState {
    const parsedQueryString = queryString.parse(props.location.search);
    const sessionId = (state.sessionId || parsedQueryString.sessionId) as string | undefined;

    return { ...state, sessionId };
  }

  ensureSessionIsValid = async () => {
    const { sessionExpired, dispatchCheckSessionAlive, dispatchSessionExpired } = this.props;

    if (sessionExpired) return;

    try {
      await dispatchCheckSessionAlive(this.state.sessionId!);
      this.debouncedEnsureSessionIsValid();
    } catch {
      dispatchSessionExpired();
    }
  };

  /**
   * Document title is used as file name when saving as PDF, add timestamp to ensure it's unique
   */
  buildDocumentTitle = (date?: boolean) => {
    const { configuration } = this.props;
    const { alignedSequences } = this.state;

    const title = [I18n.SequenceAnalyser.SequenceAnalyser.sequenceAnalyzer, configuration.app_name];
    if (alignedSequences.length > 0) {
      title.push(I18n.SequenceAnalyser.SequenceAnalyser.sequences(alignedSequences.length));
    }
    if (date) title.push(formatDatetime(new Date().toISOString(), configuration));

    return title.join(" - ");
  };

  startAlignment = () => {
    this.runningAlignmentTimer = setTimeout(() => {
      this.setState({ runningAlignment: true });
    }, DEBOUNCE_TIME.runningAlignmentLoader);
  };

  stopAlignment = () => {
    if (this.runningAlignmentTimer) {
      clearTimeout(this.runningAlignmentTimer);
      this.runningAlignmentTimer = undefined;
      this.setState({ runningAlignment: false });
    }
  };

  alignRawSequences = async () => {
    const { rawSequences } = this.state;
    const { ranAlignments } = this.props;

    this.startAlignment();

    try {
      const alignedSequences = await alignSequences(
        rawSequences.map(({ id, residues }) => ({ id, residues })),
        arrayLast(ranAlignments),
      );

      const formattedSequences = SequenceAnalyser.formatSequencesFeatures(rawSequences, alignedSequences);

      this.setSequences(rawSequences, formattedSequences, alignedSequences, { activeAlignment: true });
    } catch {
      // error handled in exceptionHandler.ts
      this.stopAlignment();
    }
  };

  handleAfterPrint = () => {
    const { location, navigate } = this.props;
    const { printing, sessionId } = this.state;

    // Ensure the user has triggered the print through the UI button
    if (printing) {
      document.title = this.buildDocumentTitle();
      navigate(`${location.pathname}?sessionId=${sessionId || ""}`, { replace: true });
      this.setState({ printing: false });
    }
  };

  updateHasColorsAndFeaturesState = () => {
    const { dispatchUpdateHasColorsAndFeaturesVisible } = this.props;
    const { colors, featureDefinitions } = this.state;

    dispatchUpdateHasColorsAndFeaturesVisible(
      colors !== undefined && hasAnyFeatureDefinitionVisible(featureDefinitions),
    );
  };

  handleFeatureDefinitionsChange = (
    featureDefinitions: FeatureDefinitions,
    featureTypesColor: FeatureTypesColor,
    userColors: SequenceFeatureColor[],
  ) => {
    this.setState({ featureDefinitions, featureTypesColor, userColors }, () => {
      this.updateHasColorsAndFeaturesState();
    });
  };

  handleColorsChange = (colors?: SelectedColorScheme) => {
    this.setState({ colors }, () => {
      this.updateHasColorsAndFeaturesState();
    });
  };

  handleViewChange = (view: SequenceAnalyserTableView) => {
    this.setState({ view });
  };

  handleSearchChange = (query: string, scope: SequencesSearchScope, selection: SequenceSelection) => {
    const { alignedSequences } = this.state;
    const matches = query.length > 0 ? searchSequences(alignedSequences, query.toUpperCase(), scope, selection) : {};
    this.setState({ search: { query, scope, matches } });
  };

  // Called when performing actions within a selection:
  // i.e. remove sequences, move sequences, insert gaps etc.
  handleAlignedSequencesChange = (
    alignedSequences: SequenceAlign[],
    sequences?: Sequence[],
    newSelection?: SequenceSelection,
  ) => {
    const { dispatchCreateSelection, dispatchClearSelection } = this.props;

    this.setState(
      (prevState) => ({
        alignedSequences: ensureAlignedSequencesSimilarLength(alignedSequences),
        columnsLetterOccurrences: calculateLetterOccurrences(alignedSequences),
        sequences: sequences || prevState.sequences,
      }),
      () => {
        dispatchClearSelection();
        if (newSelection) dispatchCreateSelection(newSelection);
      },
    );
  };

  // Called when performing insert residues action
  handleInsertSequences: HandleInsertSequences = async (
    newRawSequences: Sequence[],
    newSequences: Sequence[],
    newAlignedSequences: SequenceAlign[],
    insertPosition: number,
    sequenceType?: SequenceType,
  ) => {
    const { dispatchClearSelection } = this.props;
    const { rawSequences, alignedSequences, sequences } = this.state;

    // New sequences need to be processed to adjust features positions
    const formattedNewSequences = SequenceAnalyser.formatSequencesFeatures(newSequences, newAlignedSequences);

    const allRawSequences = [...rawSequences, ...newRawSequences];
    const allSequences = [...sequences, ...formattedNewSequences];
    const allAlignedSequences = ensureAlignedSequencesSimilarLength(
      arrayInsert(alignedSequences, newAlignedSequences, insertPosition),
    );

    await this.setSequences(allRawSequences, allSequences, allAlignedSequences);
    if (sequenceType) {
      // @ts-expect-error Just overwrite the type
      this.setState((prevState) => {
        return { ...prevState, session: { ...prevState.session, sequenceType } };
      });
    }
    dispatchClearSelection();
  };

  /**
   * Re-align given sequences. Replace existing sequences in the state by the new ones.
   */
  handleRealignment = async (options: AlignmentOptions) => {
    const { dispatchClearSelection } = this.props;
    const { rawSequences, sequences, alignedSequences } = this.state;

    this.startAlignment();

    try {
      const newAlignedSequences = await alignSequences(
        alignedSequences.map(({ id, residues }) => ({ id, residues })),
        options,
      );

      let formattedSequences = SequenceAnalyser.formatSequencesFeatures(rawSequences, newAlignedSequences);
      // Keep previous sequences when the aligned sequence is missing so we don't lose the features position
      formattedSequences = sequences.map((s) => formattedSequences.find((fs) => fs.id === s.id) || s);

      this.setSequences(rawSequences, formattedSequences, newAlignedSequences, { activeAlignment: true });

      dispatchClearSelection();
    } catch {
      // error handled in exceptionHandler.ts
      this.stopAlignment();
    }
  };

  /**
   * Remove alignment from sequences on screen using the raw sequences
   */
  handleResetAlignment = async () => {
    const { dispatchClearSelection } = this.props;
    const { rawSequences, alignedSequences } = this.state;

    const newAlignedSequences = ensureAlignedSequencesSimilarLength(
      alignedSequences.map(({ id }) => ({
        id,
        residues: (rawSequences.find(({ id: sid }) => sid === id) as Sequence).residues,
      })),
    );

    const newSequences = deepClone(rawSequences);

    // Raw sequences need to be processed to adjust features positions
    const formattedNewSequences = SequenceAnalyser.formatSequencesFeatures(newSequences, newAlignedSequences);

    await this.setSequences(rawSequences, formattedNewSequences, newAlignedSequences, { activeAlignment: false });
    dispatchClearSelection();
  };

  handleReplaceSequences: HandleReplaceSequences = async (
    newRawSequences: Sequence[],
    newSequences: Sequence[],
    newAlignedSequences: SequenceAlign[],
    toggledOppositeStrands: number[],
  ) => {
    const { dispatchClearSelection } = this.props;
    const { rawSequences, alignedSequences, sequences, oppositeStrands } = this.state;

    // New sequences need to be processed to adjust features positions
    const formattedNewSequences = SequenceAnalyser.formatSequencesFeatures(newSequences, newAlignedSequences);

    const allRawSequences = rawSequences.map((rs) => newRawSequences.find((nrs) => nrs.id === rs.id) || rs);
    const allSequences = sequences.map((s) => formattedNewSequences.find((ns) => ns.id === s.id) || s);
    const allAlignedSequences = alignedSequences.map((as) => newAlignedSequences.find((nas) => nas.id === as.id) || as);

    await this.setSequences(allRawSequences, allSequences, allAlignedSequences, {
      oppositeStrands: arraySymmetricDiff(oppositeStrands, toggledOppositeStrands),
    });
    dispatchClearSelection();
  };

  handleExpandedFeaturesForSequencesChange = (expandedFeaturesForSequences: number[]) => {
    this.setState({ expandedFeaturesForSequences });
  };

  setSequences = async (
    rawSequences: Sequence[],
    sequences: Sequence[],
    alignedSequences: SequenceAlign[],
    partialState?: Partial<ComponentState>,
  ) => {
    this.stopAlignment();

    await new Promise<void>((resolve) => {
      this.setState(
        (prevState) => {
          const output = createFeatureDefinitions(
            sequences,
            partialState?.userColors || prevState.userColors,
            prevState.featureDefinitions,
            prevState.featureTypesColor,
          );

          return {
            ...prevState,
            rawSequences,
            sequences,
            alignedSequences,
            view: SequenceAnalyserTableViews.default,
            loadingSession: false,
            runningAlignment: false,
            featureDefinitions: output.featureDefinitions,
            featureTypesColor: output.featureTypesColor,
            columnsLetterOccurrences: calculateLetterOccurrences(alignedSequences),
            alignmentVersion: prevState.alignmentVersion + 1,
            ...(partialState || {}),
          };
        },
        () => {
          resolve();
        },
      );
    });
  };

  render() {
    const {
      sequences,
      rawSequences,
      alignedSequences,
      loadingSession,
      runningAlignment,
      search,
      alignmentVersion,
      featureDefinitions,
      featureTypesColor,
      colors,
      columnsLetterOccurrences,
      view,
      sessionError,
      sessionId,
      printing,
      expandedFeaturesForSequences,
      session,
      oppositeStrands,
      activeAlignment,
      userColors,
    } = this.state;

    if (sessionError) {
      return <ErrorPage title={I18n.SequenceAnalyser.SequenceAnalyser.invalidSession} e2eId="error" />;
    }

    const maskingResidue = session?.maskingResidue || "-";

    return (
      <>
        <BrowserZoomLevelWarning />

        {sessionId && session && (
          <HeaderMenu
            title={I18n.SequenceAnalyser.SequenceAnalyser.sequenceAnalyzer}
            menu={
              loadingSession || !session.sequenceType ? (
                <div />
              ) : (
                <TableActions
                  onAlignedSequencesChange={this.handleAlignedSequencesChange}
                  onInsertSequences={this.handleInsertSequences}
                  onReplaceSequences={this.handleReplaceSequences}
                  rawSequences={rawSequences}
                  sequences={sequences}
                  alignedSequences={alignedSequences}
                  // onExport={() => this.setState({ printing: true })}
                  onResetAlignment={this.handleResetAlignment}
                  expandedFeaturesForSequences={expandedFeaturesForSequences}
                  onExpandedFeaturesForSequencesChange={this.handleExpandedFeaturesForSequencesChange}
                  sessionId={sessionId}
                  onColorSchemeChange={this.handleColorsChange}
                  colors={colors}
                  featureDefinitions={featureDefinitions}
                  featureTypesColor={featureTypesColor}
                  onFeatureDefinitionsChange={this.handleFeatureDefinitionsChange}
                  session={session}
                  userColors={userColors}
                />
              )
            }
          />
        )}

        {loadingSession && <Loading label={I18n.SequenceAnalyser.SequenceAnalyser.loadingSession} />}
        {runningAlignment && <Loading inBackdrop label={I18n.SequenceAnalyser.SequenceAnalyser.runningAlignment} />}

        {!loadingSession && session && (
          <>
            <TableActionsHeader
              view={view}
              onViewChange={this.handleViewChange}
              alignedSequences={alignedSequences}
              search={search}
              onSearchChange={this.handleSearchChange}
              sessionSequenceType={session.sequenceType}
              maskingResidue={maskingResidue}
            />

            {(printing || SHOW_PRINT_VIEW) && (
              <PrintView
                sequences={sequences}
                alignedSequences={alignedSequences}
                featureDefinitions={featureDefinitions}
                view={view}
                columnsLetterOccurrences={columnsLetterOccurrences}
                colors={colors}
                expandedFeaturesForSequences={expandedFeaturesForSequences}
                maskingResidue={maskingResidue}
              />
            )}

            <Box displayPrint="none" sx={{ ...classes.content, marginTop: `${headerPixelHeight()}px` }}>
              <DndProvider backend={HTML5Backend}>
                {alignedSequences.length === 0 ? (
                  <Box ml={2} mt={2} mb={4}>
                    <Typography>No sequences</Typography>
                  </Box>
                ) : (
                  // eslint-disable-next-line react/jsx-no-useless-fragment
                  <>
                    {!SHOW_PRINT_VIEW && (
                      <Box displayPrint={{ xs: "none" }}>
                        <SequenceTable
                          sequences={sequences}
                          alignedSequences={alignedSequences}
                          featureDefinitions={featureDefinitions}
                          colors={colors}
                          columnsLetterOccurrences={columnsLetterOccurrences}
                          view={view}
                          onAlignedSequencesChange={this.handleAlignedSequencesChange}
                          search={search}
                          alignmentVersion={alignmentVersion}
                          expandedFeaturesForSequences={expandedFeaturesForSequences}
                          oppositeStrands={oppositeStrands}
                          sessionSequenceType={session.sequenceType}
                          maskingResidue={maskingResidue}
                          activeAlignment={activeAlignment}
                        />
                        <SequenceTableVisualisations
                          colors={colors}
                          featureDefinitions={featureDefinitions}
                          sequences={sequences}
                          alignedSequences={alignedSequences}
                          columnsLetterOccurrences={columnsLetterOccurrences}
                          onInsertSequences={this.handleInsertSequences}
                          sessionSequenceType={session.sequenceType}
                        />
                        {/*<Zoom />*/}
                        <SequencesNavigatorPosition
                          alignedSequences={alignedSequences}
                          sequences={sequences}
                          featureDefinitions={featureDefinitions}
                          colors={colors}
                          expandedFeaturesForSequences={expandedFeaturesForSequences}
                        />
                      </Box>
                    )}
                  </>
                )}
              </DndProvider>
            </Box>
          </>
        )}
      </>
    );
  }
}

const mapStateToProps = (state: ApplicationState): PropsFromState => ({
  configuration: state.configuration.configuration,
  sessionExpired: state.user.sessionExpired,
  ranAlignments: state.sequenceAnalyser.ranAlignments,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  dispatchUpdateHasColorsAndFeaturesVisible: (hasColorsAndFeaturesVisible: boolean) =>
    dispatch(updateHasColorsAndFeaturesVisible(hasColorsAndFeaturesVisible)),
  dispatchRoute: (path: string) => dispatch(push(path)),
  dispatchCreateSelection: (selection: SequenceSelection) => dispatch(createSelection(selection)),
  dispatchClearSelection: () => dispatch(clearSelection()),
  dispatchCheckSessionAlive: (sessionId: string) => (dispatch as AppThunkDispatch)(checkSessionAlive(sessionId)),
  dispatchEnqueueWarningNotification: (message: string) => dispatch(enqueueWarningNotification(message)),
  dispatchSessionExpired: () => dispatch(userSessionExpired()),
});

const ConnectedSequenceAnalyser = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter<ComponentProps>(SequenceAnalyser));

export default ConnectedSequenceAnalyser;
